const AND = 'AND'
const OR = 'OR'
const NOT = 'NOT'

const ANY = 'or'
const ALL = 'and'
const NONE = 'none'

const ROOT_CONDITION = ALL
const GROUP_CONDITION = ANY

export const conditionOptions = [
  { label: 'Any', value: ANY },
  { label: 'All', value: ALL },
  { label: 'None', value: NONE },
]

export const conditionsLabels = {
  [NONE]: 'Neither',
}

export const emptyTag = () => ({ key: '', value: '' })

export const emptyGroup = () => ({ condition: GROUP_CONDITION, tags: [emptyTag()] })

const emptyTagGroups = () => ({ condition: ROOT_CONDITION, groups: [emptyGroup()] })

export function parseTagGroups(value) {
  const { condition, parts } = splitExpression(value, ROOT_CONDITION)
  const groups = parts.map(parseGroup).filter(group => group.tags.length)

  return groups.length ? { condition, groups } : emptyTagGroups()
}

export function stringifyTagGroups({ condition, groups } = {}) {
  const parts = (groups || []).map(stringifyGroup).filter(Boolean)

  if (!parts.length) {
    return ''
  }

  const expression = parts.join(separator(condition))

  return condition === NONE ? `${NOT} (${expression})` : expression
}

export function hasTag(value, tag) {
  return parseTagGroups(value).groups.some(group => group.tags.some(current => buildTag(current) === tag))
}

export function toggleTag(value, tag) {
  const rules = parseTagGroups(value)
  const { key } = parseTag(tag)

  if (hasTag(value, tag)) {
    return stringifyTagGroups({
      ...rules,
      groups: rules.groups
        .map(group => ({ ...group, tags: group.tags.filter(current => buildTag(current) !== tag) }))
        .filter(group => group.tags.length),
    })
  }

  const groupIndex = rules.groups.findIndex(group => group.tags.some(current => current.key === key))

  if (groupIndex >= 0) {
    const append = group => ({ ...group, tags: [...group.tags, parseTag(tag)] })

    return stringifyTagGroups({
      ...rules,
      groups: rules.groups.map((group, index) => index === groupIndex ? append(group) : group),
    })
  }

  return stringifyTagGroups({
    ...rules,
    groups: [...rules.groups, { condition: GROUP_CONDITION, tags: [parseTag(tag)] }],
  })
}

const separator = condition => condition === ALL ? ` ${AND} ` : ` ${OR} `

function parseGroup(part) {
  const { condition, parts } = splitExpression(part, GROUP_CONDITION)

  return { condition, tags: parts.map(parseTag).filter(tag => tag.key) }
}

function stringifyGroup({ condition, tags } = {}) {
  const parts = (tags || []).map(buildTag).filter(Boolean)

  if (!parts.length) {
    return ''
  }

  const expression = parts.join(separator(condition))

  if (condition === NONE) {
    return `${NOT} (${expression})`
  }

  return parts.length > 1 ? `(${expression})` : expression
}

function splitExpression(expression, fallback) {
  const { negated, body } = stripNegation(expression)
  const { operator, parts } = splitByOperator(body)

  return {
    condition: negated ? NONE : operator ? operator.toLowerCase() : fallback,
    parts,
  }
}

function stripNegation(expression) {
  const body = `${expression || ''}`.trim()
  const negation = body.match(/^NOT\b\s*/i)

  if (!negation) {
    return { negated: false, body: unwrapParens(body) }
  }

  const rest = body.slice(negation[0].length).trim()
  const unwrapped = unwrapParens(rest)

  if (unwrapped !== rest || splitByOperator(rest).parts.length < 2) {
    return { negated: true, body: unwrapped }
  }

  return { negated: false, body }
}

function splitByOperator(expression) {
  for (const operator of [AND, OR]) {
    const parts = splitTopLevel(expression, operator)

    if (parts.length > 1) {
      return { operator, parts }
    }
  }

  return { operator: null, parts: expression ? [expression] : [] }
}

function splitTopLevel(expression, operator) {
  const pattern = new RegExp(`^${operator}\\b`, 'i')
  const parts = []
  let depth = 0
  let current = ''

  for (let index = 0; index < expression.length; index++) {
    const char = expression[index]
    depth = Math.max(depth + (char === '(' ? 1 : char === ')' ? -1 : 0), 0)

    if (!depth && /\s/.test(char)) {
      const rest = expression.slice(index).trimStart()
      const match = rest.match(pattern)

      if (match) {
        parts.push(current.trim())
        current = ''
        index = expression.length - rest.length + match[0].length - 1
        continue
      }
    }

    current += char
  }

  parts.push(current.trim())

  return parts.filter(Boolean)
}

function unwrapParens(expression) {
  if (!expression.startsWith('(') || !expression.endsWith(')')) {
    return expression
  }

  let depth = 0

  for (let index = 0; index < expression.length; index++) {
    depth += expression[index] === '(' ? 1 : expression[index] === ')' ? -1 : 0

    if (!depth && index < expression.length - 1) {
      return expression
    }
  }

  return expression.slice(1, -1).trim()
}

function parseTag(tag) {
  const [key, ...value] = `${tag}`.split('=')

  return { key: key.trim(), value: value.join('=').trim() }
}

function buildTag({ key, value }) {
  key = `${key || ''}`.trim()
  value = `${value || ''}`.trim()

  return key && value ? `${key}=${value}` : ''
}
